import { Building2, MapPin, Shield, UserCog, Users } from 'lucide-react'

import { ALL_USERS, REGIONS, TEAMS } from '../contexts/UserContext'
import { useOrganizationData } from '../hooks/useOrganizationData'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'

interface OrgChartWidgetProps {
  compact?: boolean
}

export function OrgChartWidget({ compact = false }: OrgChartWidgetProps) {
  const orgData = useOrganizationData()

  const systemAdmins = ALL_USERS.filter((u) => u.role === 'system_admin').length
  const programAdmins = ALL_USERS.filter(
    (u) => u.role === 'program_administrator',
  ).length
  const regionalManagers = ALL_USERS.filter(
    (u) => u.role === 'regional_manager',
  ).length
  const teamManagers = ALL_USERS.filter((u) => u.role === 'manager').length
  const salespeople = ALL_USERS.filter((u) => u.role === 'user').length

  const totalPeople =
    systemAdmins + programAdmins + regionalManagers + teamManagers + salespeople

  if (compact) {
    return (
      <Card data-testid="section-org-chart">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Organization Hierarchy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-tenant-primary-color/5 w-full max-w-md rounded-lg border-2 border-primary/30 p-4 text-center">
              <div className="mb-2 flex items-center justify-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-semibold">Administrators</span>
              </div>
              <div className="text-2xl font-bold text-primary">
                {systemAdmins + programAdmins}
              </div>
            </div>

            <div className="h-4 w-0.5 bg-border" />

            <div className="w-full max-w-lg rounded-lg border-2 border-blue-500/30 bg-blue-500/5 p-4 text-center">
              <div className="mb-2 flex items-center justify-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <span className="font-semibold">Regional Managers</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {regionalManagers}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {REGIONS.length} regions
              </div>
            </div>

            <div className="h-4 w-0.5 bg-border" />

            <div className="w-full max-w-xl rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
              <div className="mb-2 flex items-center justify-center gap-2">
                <UserCog className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold">Team Managers</span>
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                {teamManagers}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {TEAMS.length} teams
              </div>
            </div>

            <div className="h-4 w-0.5 bg-border" />

            <div className="w-full rounded-lg border-2 border-amber-500/30 bg-amber-500/5 p-4 text-center">
              <div className="mb-2 flex items-center justify-center gap-2">
                <Users className="h-5 w-5 text-amber-600" />
                <span className="font-semibold">Sales Team Members</span>
              </div>
              <div className="text-2xl font-bold text-amber-600">
                {salespeople.toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                ~{Math.round(salespeople / TEAMS.length)} per team
              </div>
            </div>
          </div>

          <div className="mt-6 border-t pt-4">
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="bg-tenant-primary-color/10 rounded p-2">
                <div className="text-lg font-bold">
                  {systemAdmins + programAdmins}
                </div>
                <div className="text-xs text-muted-foreground">Admins</div>
              </div>
              <div className="rounded bg-blue-500/10 p-2">
                <div className="text-lg font-bold">{regionalManagers}</div>
                <div className="text-xs text-muted-foreground">Regional</div>
              </div>
              <div className="rounded bg-emerald-500/10 p-2">
                <div className="text-lg font-bold">{teamManagers}</div>
                <div className="text-xs text-muted-foreground">Team Mgrs</div>
              </div>
              <div className="rounded bg-amber-500/10 p-2">
                <div className="text-lg font-bold">
                  {salespeople.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">Sales</div>
              </div>
              <div className="rounded bg-muted p-2">
                <div className="text-lg font-bold">
                  {totalPeople.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">Total</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card data-testid="section-org-chart">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organization Hierarchy
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-tenant-primary-color/5 w-full max-w-md rounded-lg border-2 border-primary/30 p-4">
            <div className="mb-3 flex items-center justify-center gap-3">
              <Shield className="h-6 w-6 text-primary" />
              <div className="text-center">
                <h3 className="font-bold">Executive Leadership</h3>
                <p className="text-xs text-muted-foreground">
                  System & Program Administration
                </p>
              </div>
            </div>
            <div className="flex justify-center gap-4">
              <div className="rounded border bg-background p-2 text-center">
                <div className="text-xl font-bold text-primary">
                  {systemAdmins}
                </div>
                <div className="text-xs text-muted-foreground">
                  System Admin
                </div>
              </div>
              <div className="rounded border bg-background p-2 text-center">
                <div className="text-xl font-bold text-primary">
                  {programAdmins}
                </div>
                <div className="text-xs text-muted-foreground">
                  Program Admin
                </div>
              </div>
            </div>
          </div>

          <div className="h-6 w-0.5 bg-border" />

          <div className="w-full max-w-lg rounded-lg border-2 border-blue-500/30 bg-blue-500/5 p-4">
            <div className="mb-3 flex items-center justify-center gap-3">
              <MapPin className="h-6 w-6 text-blue-600" />
              <div className="text-center">
                <h3 className="font-bold">Regional Leadership</h3>
                <p className="text-xs text-muted-foreground">
                  {REGIONS.length} geographic regions
                </p>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {REGIONS.map((region) => (
                <div
                  key={region.id}
                  className="rounded border bg-background p-1 text-center"
                >
                  <div className="truncate text-xs font-medium">
                    {region.name}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-center">
              <Badge variant="outline">
                {regionalManagers} Regional Managers
              </Badge>
            </div>
          </div>

          <div className="h-6 w-0.5 bg-border" />

          <div className="w-full max-w-xl rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="mb-3 flex items-center justify-center gap-3">
              <UserCog className="h-6 w-6 text-emerald-600" />
              <div className="text-center">
                <h3 className="font-bold">Team Management</h3>
                <p className="text-xs text-muted-foreground">
                  {Math.round(TEAMS.length / REGIONS.length)} teams per region
                </p>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {REGIONS.map((region) => {
                const regionTeams = TEAMS.filter(
                  (t) => t.regionId === region.id,
                )
                return (
                  <div
                    key={region.id}
                    className="rounded border bg-background p-2 text-center"
                  >
                    <div className="truncate text-xs text-muted-foreground">
                      {region.name}
                    </div>
                    <div className="text-lg font-bold text-emerald-600">
                      {regionTeams.length}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-3 text-center">
              <Badge variant="outline">{teamManagers} Team Managers</Badge>
            </div>
          </div>

          <div className="h-6 w-0.5 bg-border" />

          <div className="w-full rounded-lg border-2 border-amber-500/30 bg-amber-500/5 p-4">
            <div className="mb-3 flex items-center justify-center gap-3">
              <Users className="h-6 w-6 text-amber-600" />
              <div className="text-center">
                <h3 className="font-bold">Sales Team Members</h3>
                <p className="text-xs text-muted-foreground">
                  ~{Math.round(salespeople / TEAMS.length)} members per team
                </p>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {REGIONS.map((region) => {
                const regionData = orgData.regions.find(
                  (r) => r.id === region.id,
                )
                return (
                  <div
                    key={region.id}
                    className="rounded border bg-background p-2 text-center"
                  >
                    <div className="truncate text-xs text-muted-foreground">
                      {region.name}
                    </div>
                    <div className="text-lg font-bold text-amber-600">
                      {regionData?.totalMembers || 0}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-3 text-center">
              <Badge variant="outline">
                {salespeople.toLocaleString()} Sales Members
              </Badge>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t pt-4">
          <h4 className="mb-3 text-center text-sm font-semibold">
            Organization Summary
          </h4>
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-tenant-primary-color/10 rounded-lg border border-primary/20 p-3 text-center">
              <Shield className="mx-auto mb-1 h-5 w-5 text-primary" />
              <div className="text-xl font-bold">
                {systemAdmins + programAdmins}
              </div>
              <div className="text-xs text-muted-foreground">Admins</div>
            </div>
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-center">
              <MapPin className="mx-auto mb-1 h-5 w-5 text-blue-600" />
              <div className="text-xl font-bold">{regionalManagers}</div>
              <div className="text-xs text-muted-foreground">Regional</div>
            </div>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-center">
              <UserCog className="mx-auto mb-1 h-5 w-5 text-emerald-600" />
              <div className="text-xl font-bold">{teamManagers}</div>
              <div className="text-xs text-muted-foreground">Team Mgrs</div>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-center">
              <Users className="mx-auto mb-1 h-5 w-5 text-amber-600" />
              <div className="text-xl font-bold">
                {salespeople.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">Sales</div>
            </div>
            <div className="rounded-lg border bg-muted p-3 text-center">
              <Building2 className="mx-auto mb-1 h-5 w-5" />
              <div className="text-xl font-bold">
                {totalPeople.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
